/**
 * Výčet skupin ovládacích prvků PDFEditu.
 */
export enum PdfEditFeatureGroup {
    Annotate = "Annotate",
    Anonymization = "Anonymization",
    Attachment = "Attachment",
    Callouts = "Callouts",
    Crop = "Crop",
    Default = "Default",
    Edit = "Edit",
    FormEdit = "FormEdit",
    FullScreen = "FullScreen",
    Header = "Header",
    Insert = "Insert",
    LeftPanel = "LeftPanel",
    Links = "Links",
    Menu = "Menu",
    PageNavigation = "PageNavigation",
    Print = "Print",
    Ribbons = "Ribbons",
    Search = "Search",
    Shapes = "Shapes",
    Stamps = "Stamps",
    Thumbnails = "Thumbnails",
    View = "View",
    ViewControls = "ViewControls",
    VisualSignature = "VisualSignature",
    Zoom = "Zoom",
}

/**
 * Třída reprezentující konfiguraci ovládacích prvků PDFEditu.
 */
export class PdfEditFeatures {
    /**
     * enabled - obsahuje prvky, které mají být zobrazeny
     * disabled - obsahuje prvky, které mají být defaultně skryté
     * parentGroups - nadřazené skupiny, které je pro danou skupinu také nutné povolit
     */
    private readonly _featureGroups: Map<PdfEditFeatureGroup, { enabled: string[]; disabled?: string[]; parentGroups?: PdfEditFeatureGroup[] }> = new Map<PdfEditFeatureGroup, { enabled: string[]; disabled?: string[]; parentGroups?: PdfEditFeatureGroup[] }>([
        [PdfEditFeatureGroup.Annotate, {
            enabled: [ "dropdown-item-toolbarGroup-Annotate", "toolbarGroup-Annotate", "annotationCommentButton",
                "highlightToolGroupButton", "underlineToolGroupButton", "strikeoutToolGroupButton", "squigglyToolGroupButton", "stickyToolGroupButton", "freeTextToolGroupButton",
                "textHighlightToolButton", "textUnderlineToolButton", "textSquigglyToolButton", "textStrikeoutToolButton", "textRedactToolButton" ],
            parentGroups: [ PdfEditFeatureGroup.Ribbons ],
        }],
        [PdfEditFeatureGroup.Anonymization, {
            enabled: [ "redactionToolGroupButton", "editTextModal", "annotationPopup", "annotationStylePopup", "annotationRedactButton", "annotationDeleteButton", "stylePopup", "colorPalette" ],
            disabled: [ "annotationCommentButton" ],
            parentGroups: [ PdfEditFeatureGroup.Edit ],
        }],
        [PdfEditFeatureGroup.Attachment, {
            enabled: [ "fileAttachmentToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Insert ],
        }],
        [PdfEditFeatureGroup.Callouts, {
            enabled: [ "calloutToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Insert ],
        }],
        [PdfEditFeatureGroup.Crop, {
            enabled: [ "cropToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Edit ],
        }],
        [PdfEditFeatureGroup.Default, {
            enabled: [ "passwordModal", "WarningModalClearButton", "WarningModalSignButton" ],
            disabled: [ "header", "toolsHeader", "toolsOverlay", "signatureModal", "signatureValidationModal", "customStampModal", "annotationPopup", "annotationCommentButton", "themeChangeButton", "panToolButton", "selectToolButton", "toggleNotesButton",
                "textHighlightToolButton", "textUnderlineToolButton", "textSquigglyToolButton", "textStrikeoutToolButton", "textRedactToolButton", "linkButton" ],
        }],
        [PdfEditFeatureGroup.Edit, {
            enabled: [ "toolbarGroup-Edit", "dropdown-item-toolbarGroup-Edit" ],
            disabled: [ "startFormEditingToolGroupButton", "cropToolGroupButton", "redactionToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Ribbons ],
        }],
        [PdfEditFeatureGroup.FormEdit, {
            enabled: [ "startFormEditingToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Edit ],
        }],
        [PdfEditFeatureGroup.FullScreen, {
            enabled: [ "fullscreenButton" ],
            parentGroups: [ PdfEditFeatureGroup.Menu ],
        }],
        [PdfEditFeatureGroup.Header, {
            enabled: [ "header", "toolsHeader", "undoButton", "redoButton", "eraserToolButton", "toolsOverlay" ],
        }],
        [PdfEditFeatureGroup.Insert, {
            enabled: [ "toolbarGroup-Insert", "dropdown-item-toolbarGroup-Insert" ],
            disabled: [ "signatureToolGroupButton", "rubberStampToolGroupButton", "stampToolGroupButton", "fileAttachmentToolGroupButton", "calloutToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Ribbons ],
        }],
        [PdfEditFeatureGroup.LeftPanel, {
            enabled: [ "leftPanelButton", "leftPanel", "leftPanelTabs" ],
            disabled: [ "thumbnailsPanelButton", "layersPanelButton", "documentControl", "outlinesPanelButton", "signaturePanelButton" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
        [PdfEditFeatureGroup.Links, {
            enabled: [ "linkButton" ],
            parentGroups: [ PdfEditFeatureGroup.Menu ],
        }],
        [PdfEditFeatureGroup.Menu, {
            enabled: [ "menuButton", "menuOverlay", "contextMenuPopup" ],
            disabled: [ "stickyToolButton", "highlightToolButton", "freeHandToolButton", "freeTextToolButton" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
        [PdfEditFeatureGroup.PageNavigation, {
            enabled: [ "pageNavOverlay" ],
        }],
        [PdfEditFeatureGroup.Print, {
            enabled: [ "printButton", "printModal" ],
            parentGroups: [ PdfEditFeatureGroup.Menu ],
        }],
        [PdfEditFeatureGroup.Ribbons, {
            enabled: [ "ribbons", "ribbonsDropdown" ],
            disabled: [ "toolbarGroup-View", "toolbarGroup-Annotate", "toolbarGroup-Shapes", "toolbarGroup-Insert", "toolbarGroup-Edit",
                "dropdown-item-toolbarGroup-View", "dropdown-item-toolbarGroup-Annotate", "dropdown-item-toolbarGroup-Shapes", "dropdown-item-toolbarGroup-Insert", "dropdown-item-toolbarGroup-Edit" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
        [PdfEditFeatureGroup.Search, {
            enabled: [ "searchButton", "searchPanel" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
        [PdfEditFeatureGroup.Shapes, {
            enabled: [ "dropdown-item-toolbarGroup-Shapes", "toolbarGroup-Shapes", "freeHandToolGroupButton", "shapeToolGroupButton", "ellipseToolGroupButton", "polygonToolGroupButton",
                "polygonCloudToolGroupButton", "lineToolGroupButton", "polyLineToolGroupButton", "arrowToolGroupButton", "shapeToolGroupButton", "freeHandToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Edit ],
        }],
        [PdfEditFeatureGroup.Stamps, {
            enabled: [ "rubberStampToolGroupButton", "stampToolGroupButton" ],
            parentGroups: [ PdfEditFeatureGroup.Insert ],
        }],
        [PdfEditFeatureGroup.Thumbnails, {
            enabled: [ "thumbnailsPanelButton" ],
            disabled: [ "thumbnailControl" ],
            parentGroups: [ PdfEditFeatureGroup.LeftPanel ],
        }],
        [PdfEditFeatureGroup.View, {
            enabled: [ "toolbarGroup-View", "dropdown-item-toolbarGroup-View" ],
            parentGroups: [ PdfEditFeatureGroup.Ribbons ],
        }],
        [PdfEditFeatureGroup.ViewControls, {
            enabled: [ "viewControlsButton", "viewControlsOverlay",
                "pageTransitionHeader", "continuousPageTransitionButton", "defaultPageTransitionButton", "viewControlsDivider1",
                "rotateHeader", "rotateClockwiseButton", "rotateCounterClockwiseButton", "viewControlsDivider2",
                "layoutHeader", "singleLayoutButton", "doubleLayoutButton" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
        [PdfEditFeatureGroup.VisualSignature, {
            enabled: [ "signaturePanelButton", "signaturePanel", "signatureToolGroupButton", "signatureModal", "signatureValidationModal", "customStampModal" ],
            parentGroups: [ PdfEditFeatureGroup.LeftPanel, PdfEditFeatureGroup.Insert ],
        }],
        [PdfEditFeatureGroup.Zoom, {
            enabled: [ "zoomOverlayButton", "zoomOutButton", "zoomInButton" ],
            parentGroups: [ PdfEditFeatureGroup.Header ],
        }],
    ]);

    private _features: Map<string, boolean> = new Map<string, boolean>();
    private _enabledFeatureGroups: PdfEditFeatureGroup[] = [];

    /**
     * Konstruktor konfiguračního objektu pro PDFEdit.
     */
    constructor() {
        this.enableFeatureGroup(PdfEditFeatureGroup.Default);
    }

    /**
     * Povolí ovládací prvky podle skupiny.
     * @param featureGroup Skupina ovládacích prvků, která se má povolit.
     */
    public enableFeatureGroup(featureGroup: PdfEditFeatureGroup): void {
        if (!featureGroup || this._enabledFeatureGroups.includes(featureGroup))
            return;

        const features = this._featureGroups.get(featureGroup);
        if (features) {
            for (const feature of features.enabled) {
                this._features.set(feature, true);
            }
            if (features.disabled) {
                for (const feature of features.disabled) {
                    if (!this._features.has(feature)) {
                        this._features.set(feature, false);
                    }
                }
            }
            this._enabledFeatureGroups.push(featureGroup);
            if (features.parentGroups) {
                for (const parentGroup of features.parentGroups)
                    this.enableFeatureGroup(parentGroup);
            }
        }
    }

    /**
     * Vrací pole zakázaných elementů.
     */
    public get disabledElements(): string[] {
        return [...this._features.entries()].filter(pair => !pair[1]).map(([key]) => key);
    }

    /**
     * Vrací pole povolených elementů.
     */
    public get enabledElements(): string[] {
        return [...this._features.entries()].filter(pair => pair[1]).map(([key]) => key);
    }
}
